import AgencyHelper, { AgencyConstants } from 'features/Agency/agencyHelper';
import ChargeHelper, { ChargeConstants } from 'features/Charge/chargeHelper';
import DropdownFilterTable from 'general/components/DropdownFilterTable';
import { KTBSDropdownAlignments } from 'general/components/OtherKeenComponents/KTBSDropdown';
import KTDropdownFilter from 'general/components/OtherKeenComponents/KTDropdownFilter';
import KTTooltip from 'general/components/OtherKeenComponents/KTTooltip';
import CustomDateRangePicker, {
  dateFilter,
  getDateFilterOption,
} from 'general/components/Others/CustomDateRangePicker';
import CustomDropdown2 from 'general/components/Others/CustomDropdown2';
import EmptyView from 'general/components/Others/EmptyView';
import DashboardChartCard from 'general/components/quybka/Cards/DashboardChartCard';
import BarChart from 'general/components/quybka/Charts/BarChart';
import DoughnutChartJS from 'general/components/quybka/Charts/DoughnutChartJS';
import AppColors from 'general/constants/AppColors';
import AppResource from 'general/constants/AppResource';
import AppRoute from 'general/constants/AppRoute';
import UIHelper from 'general/helpers/UIHelper';
import Utils from 'general/utils/Utils';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import imgEmptyNoData from 'assets/images/img_emptyview_no_data.png';
import CustomCardMore from 'general/components/Others/CustomCardMore';

InvestorDashboardScreen.propTypes = {
  agencyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

function InvestorDashboardScreen({ agencyId }) {
  // MARK: ---- PROPERTIES ----
  const notificationCardRef = useRef();
  const [agencyDetail, setAgencyDetail] = useState(null);
  useEffect(() => {
    if (Utils.isValid(agencyId)) {
      AgencyHelper.API.requestAgencyDetail(agencyId).then((response) => {
        setAgencyDetail(response);
      });
    }
  }, [agencyId]);
  // thong tin tru sac
  const [chargePointStatistic, setChargePointStatistic] = useState({
    total: 0,
    charging: 0,
    connected: 0,
  });
  // thong tin he thong
  const [systemStatistic, setSystemStatistic] = useState({
    totalEnergy: 0, // san luong dien trong ngay
    totalPower: 0, // cong suat tuc thi cua he thong
    totalTransactions: 0, // tong so phien sac trong ngay
    totalChargeDuration: 0, // tong thoi gian sac trong ngay
  });
  useEffect(() => {
    requestGetRealtimeData();
    const timer = setInterval(() => {
      requestGetRealtimeData();
    }, 5000);
    return () => {
      clearInterval(timer);
    };
  }, []);
  // thong ke tru sac
  const [chargePointSummary, setChargePointSummary] = useState({});
  const [selectedChargeStation, setSelectedChargeStation] = useState({
    id: null,
    name: 'Tất cả trạm',
    item: null,
    address: '',
  });
  const filterChargeStationEl = useMemo(() => {
    return (
      <CustomDropdown2
        id="station_filter"
        name="station_filter"
        toggleElement={UIHelper.buildWhiteButton(
          <div className="d-flex align-items-center gap-2">
            <span>
              {_.truncate(selectedChargeStation?.name ?? 'Tất cả trạm', {
                length: 20,
                separator: '...',
              })}
            </span>{' '}
            <i className="fa-solid fa-caret-down" />
          </div>,
          () => { console.log(''); },
          <div className="position-relative">
            <i className="fa-regular fa-filter" style={{ color: AppColors.gray[700] }} />
            {Utils.isValid(selectedChargeStation?.id) && (
              <i
                className="position-absolute fa-solid fa-circle"
                style={{
                  color: 'orange',
                  top: '-2px',
                  right: '-4px',
                  fontSize: '7px',
                }}
              />
            )}
          </div>,
          false,
          false,
          10
        )}
        selectedValue={selectedChargeStation}
        dropdownMenuItems={[{ name: 'Trạm sạc', role: 'STATION' }]}
        subTitleType="address"
        getSubItems={async (item) => {
          return ChargeHelper.API.requestGetChargeStationList({
            page: item?.page || 0,
            limit: 10,
            q: item?.q || '',
          }).then((data) => {
            let values = data.chargeStations?.map((station) => ({
              id: station?.stationId,
              name: station?.name,
              item: station,
              address: ChargeHelper.Utils.getChargeStationFullAddress(station),
            }));
            if (!Utils.isValid(item?.page) || _.toNumber(item?.page) === 0) {
              values = [
                {
                  id: null,
                  name: 'Tất cả trạm',
                  item: null,
                  address: '',
                },
              ].concat(values);
            }
            return {
              values: values,
              total: data.total,
            };
          });
        }}
        dropdownMenuStyle={{ maxHeight: '30vh' }}
        dropdownMenuClassName="overflow-auto w-300px"
        alignment={KTBSDropdownAlignments.end}
        onChange={(value) => {
          setSelectedChargeStation(value);
        }}
        isDropdownForm={true}
        titleSearchable={true}
        searchable={true}
        searchFormClassName="mx-3 my-1 search-form-border"
        ignoreConfirmClick={true}
        defaultValue={{
          name: 'Trạm sạc',
          role: 'STATION',
        }}
      />
    );
  }, [selectedChargeStation]);
  useEffect(() => {
    let params = { agencyId: agencyId };
    if (Utils.isValid(selectedChargeStation?.id)) {
      params.stationId = selectedChargeStation?.id;
    }
    ChargeHelper.API.Dashboard.requestGetPointSummary(params).then((res) => {
      setChargePointSummary(res ?? {});
    });
  }, [selectedChargeStation, agencyId]);
  // bieu do
  const [chartData, setChartData] = useState({});
  const [chartFilter, setChartFilter] = useState({
    from: getDateFilterOption(dateFilter.baMuoiNgayGanNhat)[0].format('YYYY-MM-DD'),
    to: getDateFilterOption(dateFilter.baMuoiNgayGanNhat)[1].format('YYYY-MM-DD'),
    selectedValue: dateFilter.baMuoiNgayGanNhat,
  });
  useEffect(() => {
    ChargeHelper.API.Dashboard.requestGetDailyData({
      from: chartFilter.from,
      to: chartFilter.to,
    }).then((res) => {
      setChartData(res ?? {});
    });
  }, [chartFilter]);

  useEffect(() => {
    setEnergyFilter({ ...chartFilter, energyType: energyType });
    setTransactionFilter({ ...chartFilter, transactionType: transactionType });
    setEnergyRevenueFilter({ ...chartFilter, energyRevenueType: energyRevenueType });
  }, [chartFilter])

  const processVehicleData = (data) => {
    if (!data) return;
    const transactionCount = {
      CAR: [],
      BIKE: []
    };
    const totalRevenue = {
      CAR: [],
      BIKE: []
    };
    const totalEnergy = {
      CAR: [],
      BIKE: []
    };

    data?.forEach(day => {
      const date = day.date;

      const carData = day.vehicleTypes.find(v => v.vehicleType === "CAR");
      const bikeData = day.vehicleTypes.find(v => v.vehicleType === "BIKE");

      transactionCount.CAR.push({
        date: date,
        transactions: carData.transactionCount || 0
      });
      transactionCount.BIKE.push({
        date: date,
        transactions: bikeData.transactionCount || 0
      });

      totalRevenue.CAR.push({
        date: date,
        amount: carData.totalRevenue || 0
      });
      totalRevenue.BIKE.push({
        date: date,
        amount: bikeData.totalRevenue || 0
      });

      totalEnergy.CAR.push({
        date: date,
        energy: carData.totalEnergy || 0
      });
      totalEnergy.BIKE.push({
        date: date,
        energy: bikeData.totalEnergy || 0
      });
    });

    return {
      transactionCount,
      totalRevenue,
      totalEnergy
    };
  }

  const chartDataByVehicle = useMemo(() => {
    return processVehicleData(chartData?.dailyByVehicleType);
  }, [chartData])

  const mergeDataByDate = (dataByCar = [], dataByBike = [], dataName) => {
    const allDates = Array.from(
      new Set([...dataByCar.map(item => item.date), ...dataByBike.map(item => item.date)])
    );

    return allDates.map(date => {
      const carAmount = dataByCar.find(item => item.date === date)?.[dataName] || 0;
      const bikeAmount = dataByBike.find(item => item.date === date)?.[dataName] || 0;

      return { date, [dataName]: carAmount + bikeAmount };
    }).sort((a, b) => new Date(a.date) - new Date(b.date));
  };

  const getDataByType = (type, vehicleType, chartData, chartDataByVehicle) => {
    const typeMap = {
      transaction: {
        '': mergeDataByDate(
          chartDataByVehicle?.transactionCount?.CAR,
          chartDataByVehicle?.transactionCount?.BIKE,
          'transactions'
        ),
        BIKE: chartDataByVehicle?.transactionCount?.BIKE,
        CAR: chartDataByVehicle?.transactionCount?.CAR
      },
      energy: {
        '': mergeDataByDate(
          chartDataByVehicle?.totalEnergy?.CAR,
          chartDataByVehicle?.totalEnergy?.BIKE,
          'energy'
        ),
        BIKE: chartDataByVehicle?.totalEnergy?.BIKE,
        CAR: chartDataByVehicle?.totalEnergy?.CAR
      },
      revenue: {
        '': mergeDataByDate(
          chartDataByVehicle?.totalRevenue?.CAR,
          chartDataByVehicle?.totalRevenue?.BIKE,
          'amount'
        ),
        BIKE: chartDataByVehicle?.totalRevenue?.BIKE,
        CAR: chartDataByVehicle?.totalRevenue?.CAR
      }
    };

    return typeMap[type]?.[vehicleType] || null;
  };

  const [energyType, setEnergyType] = useState('');
  const [energyFilter, setEnergyFilter] = useState({ ...chartFilter, energyType: energyType });
  // dien sac theo ngay
  const chartEnergyByDateEl = useMemo(() => {
    const energyByDate = getDataByType('energy', energyType, chartData, chartDataByVehicle);
    if (!energyByDate) return;
    let labels = energyByDate?.map((item) => Utils.formatDateTimeToString(item.date, 'DD'));
    let data = energyByDate?.map((item) => ((item.energy ?? 0) / 1000).toFixed(3));
    if (energyByDate?.length > 31) {
      const groupedData = _.groupBy(energyByDate, (item) => moment(item.date).format('MM/YYYY'));
      labels = Object.keys(groupedData);
      data = labels?.map((label) => {
        const total = groupedData[label]?.reduce((sum, item) => sum + item.energy, 0);
        return (total / 1000).toFixed(3); // Chuyển đổi sang kWh
      });
    }

    return (
      <DashboardChartCard
        title="Thống kê điện sạc"
        name="Tổng"
        value={
          Utils.formatNumber(data?.reduce((sum, item) => _.toNumber(sum) + _.toNumber(item), 0)?.toFixed(3)) +
          ' kWh'
        }
        valueColor="rgba(41, 132, 238, 1)"
        actionEl={
          <DropdownFilterTable
            id="energyType"
            isLightButton={true}
            toggleElementBorderRadius={10}
            filterComponent={[
              <div className="col-12 p-0">
                <KTDropdownFilter
                  label="Loại điện sạc"
                  filterKey="energyType"
                  options={[
                    { name: 'Tất cả', value: '' },
                    ...ChargeHelper.Data.vehicleTypeOptions,
                  ]}
                  selectedValue={energyFilter.energyType}
                  handleChange={(value) => {
                    setEnergyFilter({ ...energyFilter, energyType: value });
                  }}
                />
              </div>,
              <div className="col-12 p-0">
                <label htmlFor="filterByDate" className="mb-0 mr-2 font-weight-bolder">
                  Khoảng thời gian thống kê
                </label>
                <CustomDateRangePicker
                  alignRight={true}
                  toggleFontWeight="600"
                  toggleElementBorderRadius={5}
                  initialValue={energyFilter.selectedValue}
                  onPressSave={(selectedValue, startDate, endDate) => {
                    setEnergyFilter({
                      ...energyFilter,
                      from: startDate,
                      to: endDate,
                      selectedValue: selectedValue,
                    });
                  }}
                />
              </div>
            ]}
            resetDefaultTable={() => {
              const updateChartFilter = {
                ...chartFilter,
                from: getDateFilterOption(dateFilter.baMuoiNgayGanNhat)[0].format('YYYY-MM-DD'),
                to: getDateFilterOption(dateFilter.baMuoiNgayGanNhat)[1].format('YYYY-MM-DD'),
                selectedValue: dateFilter.baMuoiNgayGanNhat,
              };
              setEnergyFilter({ ...updateChartFilter, energyType: '' });
              setEnergyType('');
              setChartFilter(updateChartFilter);
            }}
            saveFilter={() => {
              const { energyType, ...dateFilter } = energyFilter;
              setChartFilter(dateFilter);
              setEnergyType(energyType);
            }}
            isDefaultFilter={Utils.checkDefaultFilter(
              ['selectedValue', 'from', 'to'],
              { ...chartFilter, energyType },
              dateFilter.baMuoiNgayGanNhat,
            )}
          />
        }
        chartEl={
          <BarChart
            title="Điện sạc"
            unit="kWh"
            labels={labels}
            data={data}
            color="rgba(41, 132, 238, 0.4)"
            hoverColor="rgba(41, 132, 238, 1)"
          />
        }
      />
    );
  }, [chartDataByVehicle, chartData, chartFilter, energyFilter, energyType]);

  // phien sac theo ngay
  const [transactionType, setTransactionType] = useState('');
  const [transactionFilter, setTransactionFilter] = useState({ ...chartFilter, transactionType: transactionType });
  const chartTransactionByDateEl = useMemo(() => {
    const transactionByDate = getDataByType('transaction', transactionType, chartData, chartDataByVehicle);
    if (!transactionByDate) return;
    let labels = transactionByDate?.map((item) => Utils.formatDateTimeToString(item.date, 'DD'));
    let data = transactionByDate?.map((item) => item.transactions ?? 0);

    if (transactionByDate?.length > 31) {
      const groupedData = _.groupBy(transactionByDate, (item) =>
        moment(item.date).format('MM/YYYY')
      );
      labels = Object.keys(groupedData);
      data = labels?.map((label) => {
        const total = groupedData[label]?.reduce((sum, item) => sum + item.transactions, 0);
        return total;
      });
    }
    return (
      <DashboardChartCard
        title="Thống kê phiên sạc"
        name="Tổng"
        value={
          Utils.formatNumber(data?.reduce((sum, item) => _.toNumber(sum) + _.toNumber(item), 0)) +
          ' phiên sạc'
        }
        valueColor="rgba(88, 86, 214, 1)"
        actionEl={
          <DropdownFilterTable
            id="transactionType"
            isLightButton={true}
            toggleElementBorderRadius={10}
            filterComponent={[
              <div className="col-12 p-0">
                <KTDropdownFilter
                  label="Loại phiên sạc"
                  filterKey="transactionType"
                  options={[
                    { name: 'Tất cả', value: '' },
                    ...ChargeHelper.Data.vehicleTypeOptions,
                  ]}
                  selectedValue={transactionFilter.transactionType}
                  handleChange={(value) => {
                    setTransactionFilter({ ...transactionFilter, transactionType: value });
                  }}
                />
              </div>,
              <div className="col-12 p-0">
                <label htmlFor="filterByDate" className="mb-0 mr-2 font-weight-bolder">
                  Khoảng thời gian thống kê
                </label>
                <CustomDateRangePicker
                  alignRight={true}
                  toggleFontWeight="600"
                  toggleElementBorderRadius={5}
                  initialValue={transactionFilter.selectedValue}
                  onPressSave={(selectedValue, startDate, endDate) => {
                    setTransactionFilter({
                      ...transactionFilter,
                      from: startDate,
                      to: endDate,
                      selectedValue: selectedValue,
                    });
                  }}
                />
              </div>
            ]}
            resetDefaultTable={() => {
              const updateChartFilter = {
                ...chartFilter,
                from: getDateFilterOption(dateFilter.baMuoiNgayGanNhat)[0].format('YYYY-MM-DD'),
                to: getDateFilterOption(dateFilter.baMuoiNgayGanNhat)[1].format('YYYY-MM-DD'),
                selectedValue: dateFilter.baMuoiNgayGanNhat,
              };
              setTransactionFilter({ ...updateChartFilter, transactionType: '' });
              setTransactionType('');
              setChartFilter(updateChartFilter);
            }}
            saveFilter={() => {
              const { transactionType, ...dateFilter } = transactionFilter;
              setChartFilter(dateFilter);
              setTransactionType(transactionType);
            }}
            isDefaultFilter={Utils.checkDefaultFilter(
              ['selectedValue', 'from', 'to'],
              { ...chartFilter, transactionType },
              dateFilter.baMuoiNgayGanNhat,
            )}
          />
        }
        chartEl={
          <BarChart
            title="Phiên sạc"
            unit="phiên sạc"
            labels={labels}
            data={data}
            color="rgba(88, 86, 214, 0.4)"
            hoverColor="rgba(88, 86, 214, 1)"
          />
        }
      />
    );
  }, [chartDataByVehicle, chartData, chartFilter, transactionFilter, transactionType]);

  // doanh thu ban dien
  const [energyRevenueType, setEnergyRevenueType] = useState('');
  const [energyRevenueFilter, setEnergyRevenueFilter] = useState({ ...chartFilter, energyRevenueType: energyRevenueType });
  const chartEnergyRevenueByDateEl = useMemo(() => {
    const energyRevenueByDate = getDataByType('revenue', energyRevenueType, chartData, chartDataByVehicle);
    if (!energyRevenueByDate) return;
    let labels = energyRevenueByDate?.map((item) => Utils.formatDateTimeToString(item.date, 'DD'));
    let data = energyRevenueByDate?.map((item) => item.amount ?? 0);

    if (energyRevenueByDate?.length > 31) {
      const groupedData = _.groupBy(energyRevenueByDate, (item) => moment(item.date).format('MM/YYYY'));
      labels = Object.keys(groupedData);
      data = labels?.map((label) => {
        const total = groupedData[label]?.reduce((sum, item) => sum + item.amount, 0);
        return total;
      });
    }

    return (
      <DashboardChartCard
        title="Thống kê doanh thu bán điện"
        name="Tổng"
        value={
          Utils.formatNumber(data?.reduce((sum, item) => _.toNumber(sum) + _.toNumber(item), 0)) +
          ' VNĐ'
        }
        valueColor="#30BDA8"
        actionEl={
          <DropdownFilterTable
            id="energyRevenueType"
            isLightButton={true}
            toggleElementBorderRadius={10}
            filterComponent={[
              <div className="col-12 p-0">
                <KTDropdownFilter
                  label="Loại doanh thu"
                  filterKey="energyRevenueType"
                  options={[
                    { name: 'Tất cả', value: '' },
                    ...ChargeHelper.Data.vehicleTypeOptions,
                  ]}
                  selectedValue={energyRevenueFilter.energyRevenueType}
                  handleChange={(value) => {
                    setEnergyRevenueFilter({ ...energyRevenueFilter, energyRevenueType: value });
                  }}
                />
              </div>,
              <div className="col-12 p-0">
                <label htmlFor="filterByDate" className="mb-0 mr-2 font-weight-bolder">
                  Khoảng thời gian thống kê
                </label>
                <CustomDateRangePicker
                  alignRight={true}
                  toggleFontWeight="600"
                  toggleElementBorderRadius={5}
                  initialValue={energyRevenueFilter.selectedValue}
                  onPressSave={(selectedValue, startDate, endDate) => {
                    setEnergyRevenueFilter({
                      ...energyRevenueFilter,
                      from: startDate,
                      to: endDate,
                      selectedValue: selectedValue,
                    });
                  }}
                />
              </div>
            ]}
            resetDefaultTable={() => {
              const updateChartFilter = {
                ...chartFilter,
                from: getDateFilterOption(dateFilter.baMuoiNgayGanNhat)[0].format('YYYY-MM-DD'),
                to: getDateFilterOption(dateFilter.baMuoiNgayGanNhat)[1].format('YYYY-MM-DD'),
                selectedValue: dateFilter.baMuoiNgayGanNhat,
              };
              setEnergyRevenueFilter({ ...updateChartFilter, energyRevenueType: '' });
              setEnergyRevenueType('');
              setChartFilter(updateChartFilter);
            }}
            saveFilter={() => {
              const { energyRevenueType, ...dateFilter } = energyRevenueFilter;
              setChartFilter(dateFilter);
              setEnergyRevenueType(energyRevenueType);
            }}
            isDefaultFilter={Utils.checkDefaultFilter(
              ['selectedValue', 'from', 'to'],
              { ...chartFilter, energyRevenueType },
              dateFilter.baMuoiNgayGanNhat,
            )}
          />
        }
        chartEl={
          <BarChart
            title="Doanh thu"
            unit="VNĐ"
            labels={labels}
            data={data}
            color="#30BDA84D"
            hoverColor="#30BDA8"
            showLine={true}
          />
        }
      />
    );
  }, [chartDataByVehicle, chartData, chartFilter, energyRevenueFilter, energyRevenueType]);

  // tong quan nha dau tu NQ
  const [investorSummary, setInvestorSummary] = useState({});
  useEffect(() => {
    if (Utils.isValid(agencyId)) {
      AgencyHelper.API.requestGetInvestorSummary(agencyId).then((response) => {
        setInvestorSummary(response);
      });
    }
  }, [agencyId]);

  // uoc tinh hoa hong cho NĐT nhuong quyen
  const [monthlyInvestorCommission, setMonthlyInvestorCommission] = useState({});
  useEffect(() => {
    if (!agencyId) return;
    ChargeHelper.API.Dashboard.requestGetMonthlyInvestorCommission({
      agencyId: agencyId,
      month: moment().format('YYYY-MM'),
    }).then((res) => {
      setMonthlyInvestorCommission(res);
    });
  }, [agencyId]);

  // doi soat NĐT NQ
  const [investorReconciliation, setInvestorReconciliation] = useState([]);
  useEffect(() => {
    if (!agencyId) return;
    AgencyHelper.API.requestGetReconciliationList({
      agencyId: agencyId,
      forRole: AgencyConstants.AgencyRole.INVESTOR,
    }).then((res) => {
      setInvestorReconciliation(res?.reconciliations);
    });
  }, [agencyId]);

  // thong bao he thong
  const [filterSystemLog, setFilterSystemLog] = useState({
    page: 0,
    limit: 10,
    selectedValue: dateFilter.baMuoiNgayGanNhat,
    from: getDateFilterOption(dateFilter.baMuoiNgayGanNhat)[0].format('YYYY-MM-DD'),
    to: getDateFilterOption(dateFilter.baMuoiNgayGanNhat)[1].format('YYYY-MM-DD'),
    stationId: '',
    agencyId: agencyId,
  });
  const [filterSystemLogDropdown, setFilterSystemLogDropdown] = useState({
    page: 0,
    limit: 10,
    selectedValue: dateFilter.baMuoiNgayGanNhat,
    from: getDateFilterOption(dateFilter.baMuoiNgayGanNhat)[0].format('YYYY-MM-DD'),
    to: getDateFilterOption(dateFilter.baMuoiNgayGanNhat)[1].format('YYYY-MM-DD'),
    stationId: '',
    agencyId: agencyId,
  });
  const [selectedChargeStationSystemLog, setSelectedChargeStationSystemLog] = useState(null);
  const [systemLogList, setSystemLogList] = useState([]);
  const [totalSystemLog, setTotalSystemLog] = useState(0);

  useEffect(() => {
    const params = _.pickBy(
      filterSystemLog,
      (value) => value !== null && value !== undefined && value !== ''
    );

    ChargeHelper.API.SystemLog.requestGetSystemLogList(params)
      .then((response) => {
        setSystemLogList((prevList) => {
          if (filterSystemLog?.page !== 0) {
            const existingIds = new Set(prevList.map(item => item.messageId));
            const newUnique = (response?.messages ?? []).filter(
              item => !existingIds.has(item.messageId)
            );

            return [...prevList, ...newUnique];
          } else {
            return response?.messages ?? [];
          }
        });

        setTotalSystemLog(response?.total ?? 0);
      })
      .catch((error) => {
        console.error("Lỗi khi lấy system log:", error);
      });
  }, [filterSystemLog]);

  const filterChargeStationSystemLogEl = useMemo(() => {
    return (
      <CustomDropdown2
        id="stationId_filter"
        name="stationId_filter"
        toggleElement={
          <button
            className={`btn btn-white border-secondary dropdown-toggle w-100 d-flex align-items-center justify-content-between`}
            type="button"
          >
            {selectedChargeStationSystemLog?.name ?? 'Chọn trạm sạc'}
          </button>
        }
        selectedValue={selectedChargeStationSystemLog}
        dropdownMenuItems={[{ name: 'Trạm sạc', role: 'STATION' }]}
        subTitleType="address"
        getSubItems={async (item) => {
          return ChargeHelper.API.requestGetChargeStationList({
            page: item?.page || 0,
            limit: 10,
            q: item?.q || '',
          }).then((data) => {
            return {
              values: data.chargeStations?.map((station) => ({
                id: station?.stationId,
                name: station?.name,
                item: station,
                address: ChargeHelper.Utils.getChargeStationFullAddress(station),
              })),
              total: data.total,
            };
          });
        }}
        dropdownMenuStyle={{ maxHeight: '30vh' }}
        dropdownMenuClassName="overflow-auto w-100"
        onChange={(value) => {
          setSelectedChargeStationSystemLog(value);
          setFilterSystemLogDropdown({
            ...filterSystemLogDropdown,
            stationId: value?.id,
          });
        }}
        isDropdownForm={true}
        titleSearchable={true}
        searchable={true}
        searchFormClassName="mx-3 my-1 search-form-border"
        ignoreConfirmClick={true}
        defaultValue={{
          name: 'Trạm sạc',
          role: 'STATION',
        }}
        footerEl={
          <div
            className="p-4 position-sticky sticky-bottom bg-white"
            style={{
              borderTop: `1px solid ${AppColors.gray[300]}`,
            }}
          >
            {UIHelper.buildNegativeButton('Bỏ chọn', () => {
              setSelectedChargeStationSystemLog(null);
              setFilterSystemLogDropdown({
                ...filterSystemLogDropdown,
                stationId: '',
              });
            })}
          </div>
        }
      />
    );
  }, [selectedChargeStationSystemLog]);

  // MARK: ---- METHODS ----
  const requestGetRealtimeData = useCallback(() => {
    ChargeHelper.API.Dashboard.requestGetRealtimeData().then((res) => {
      setChargePointStatistic({
        total: res?.totalChargePoint ?? 0,
        charging: res?.chargingChargePoint ?? 0,
        connected: res?.connectedChargePoint ?? 0,
      });
      setSystemStatistic({
        totalEnergy: res?.totalEnergy ?? 0,
        totalPower: res?.totalPower ?? 0,
        totalTransactions: res?.totalTransactions ?? 0,
        totalChargeDuration: res?.totalChargeDuration ?? 0,
      });
    });
  }, []);

  const InvestorReconciliationTable = (data) => {
    const textHeader = ['Tháng', 'Số tiền'];

    const contents = data?.map(item => ({
      name: `Tháng ${Utils.formatDateTimeToString(item?.month, 'MM/YYYY')}`,
      value: (item?.totalRevenue || 0) - (item?.taxAmount || 0)
    })) || [];

    let html = `
    <div>
      Mức doanh thu của những tháng chưa ký đối soát.
      <div class="row m-0 position-relative py-2 bg-white rounded-lg">
        <div class="rounded-lg p-0" style="border: 1px solid #EBEDEF">
          <table class="w-100" style="border-collapse: collapse">
            <thead>
              <tr style="background-color: #F7F7FD; font-size: 12px; color: #494968">
  `;

    textHeader.forEach((header, index) => {
      const align = index === 0 ? 'text-left' : 'text-right';
      const minWidth = index === 0 ? '200px' : '100px';
      html += `
      <th class="p-3 ${align}" style="min-width: ${minWidth}; font-weight: bold; padding: 12px 16px;">
        ${header}
      </th>`;
    });

    html += `
              </tr>
            </thead>
            <tbody>`;

    if (contents.length > 0) {
      contents.forEach(row => {
        html += `
      <tr style="border-top: 1px solid #EBEDEF">
        <td class="p-3 text-left" style="padding: 12px 16px;">${row.name ?? '--'}</td>
        <td class="p-3 text-right" style="padding: 12px 16px;">
          ${Utils.formatNumber(row.value)} VNĐ
        </td>
      </tr>`;
      });
    } else {
      html += `
      <tr>
        <td colspan="2" class="p-3 text-center">Không có dữ liệu để hiển thị</td>
      </tr>`;
    }

    html += `
            </tbody>
          </table>
        </div>
      </div>
    </div>`;

    return html;
  };

  return (
    <div className="mt-4" style={{ minHeight: '1200px' }}>
      <div className="row m-0 p-0 px-2">
        <div className="col-lg-6 mb-4 px-2">
          {UIHelper.buildCard(
            <div>
              <div
                className="d-flex flex-column p-4"
                style={{
                  borderBottom: `1px solid ${AppColors.gray[200]}`,
                }}
              >
                <span style={UIHelper.buildFontStyle('700', '30px', AppColors.gray[600])}>
                  {agencyDetail?.name}
                </span>
                <span style={UIHelper.buildFontStyle('700', '26px', AppColors.primary)}>
                  NĐT nhượng quyền
                </span>
              </div>
              <div className="d-flex flex-column gap-3 pt-2 pb-6">
                <div className="px-4 d-flex flex-column gap-3 mt-4">
                  {UIHelper.buildStatisticItem(
                    'rgba(134, 118, 255, 1)',
                    <div className="d-flex align-items-center gap-2">
                      Tổng số trạm sạc{' '}
                      <KTTooltip
                        text={`<div><span>Trạm sạc ô tô: ${Utils.formatNumber(
                          investorSummary?.carStations ?? 0
                        )}</span><br /><span>Trạm sạc xe máy: ${Utils.formatNumber(
                          investorSummary?.bikeStations ?? 0
                        )}</span></div>`}
                        textHeader="Số trạm sạc"
                        isHtmlContent
                      >
                        <i className="fa-regular fa-info-circle" style={{ color: AppColors.primary }} />
                      </KTTooltip>
                    </div>,
                    Utils.formatNumber(investorSummary?.totalStations ?? 0),
                    null,
                    'fa-solid fa-charging-station'
                  )}
                  {UIHelper.buildStatisticItem(
                    'rgba(255, 109, 112, 1)',
                    <div className="d-flex align-items-center gap-2">
                      Tổng chi phí đầu tư{' '}
                      <KTTooltip
                        text="Với đơn hàng nhượng quyền: Chi phí đầu tư được tính dựa trên giá trị sản phẩm trụ sạc đã bán trước thuế, không tính giá trị trang thiết bị & dịch vụ có trong đơn hàng."
                        textHeader="Tổng chi phí đầu tư"
                        isHtmlContent
                      >
                        <i className="fa-regular fa-info-circle" style={{ color: AppColors.primary }} />
                      </KTTooltip>
                    </div>,
                    `${Utils.formatNumber(investorSummary?.totalInvestment ?? 0)} VNĐ`,
                    null,
                    'fa-solid fa-chart-line-up'
                  )}
                  {UIHelper.buildStatisticItem(
                    '#FD7497',
                    <div className="d-flex align-items-center gap-2">
                      Doanh thu dự kiến trong tháng{' '}
                      <KTTooltip
                        text={InvestorReconciliationTable(
                          investorReconciliation?.filter(r => r?.status !== 'COMPLETED')
                        )}
                        textHeader="Doanh thu dự kiến"
                        isHtmlContent
                      >
                        <i className="fa-regular fa-info-circle" style={{ color: AppColors.primary }} />
                      </KTTooltip>
                    </div>,
                    Utils.formatNumber(Math.round(monthlyInvestorCommission?.totalEnergyCommission ?? 0)) + ' VNĐ',
                    AppResource.icons.icExpectedRevenue,
                    null
                  )}
                  {UIHelper.buildStatisticItem(
                    'rgba(48, 189, 111, 1)',
                    'Tổng doanh thu đã nhận',
                    `${Utils.formatNumber(investorSummary?.totalRevenue ?? 0)} VNĐ`,
                    null,
                    'fa-solid fa-chart-mixed'
                  )}
                </div>
              </div>
            </div>,
            false
          )}
        </div>
        {/* thong bao */}
        <div className="col-lg-6 px-2 mb-4">
          {UIHelper.buildCard(
            <div>
              <div className='d-flex justify-content-between'>
                {UIHelper.buildCardHeader(
                  'Thông báo vận hành',
                  false,
                  null,
                  false,
                  true,
                  <span style={UIHelper.buildFontStyle('600', '13px', AppColors.gray[800])}>
                    Tổng:{' '}
                    <span style={{ color: AppColors.primary }}>
                      {Utils.formatNumber(totalSystemLog)} thông báo
                    </span>
                  </span>
                )}
                <div className='px-4 py-3'>
                  <DropdownFilterTable
                    id="systemLog"
                    isLightButton={true}
                    toggleElementBorderRadius={10}
                    filterComponent={[
                      <div className="col-12 pb-2 p-0">
                        {UIHelper.buildFormLabel('Trạm sạc')}
                        {filterChargeStationSystemLogEl}
                      </div>,
                      <div className="col-12 p-0">
                        <label htmlFor="filterByDate" className="mb-0 mr-2 font-weight-bolder">
                          Khoảng thời gian thống kê
                        </label>
                        <CustomDateRangePicker
                          alignRight={true}
                          toggleFontWeight="600"
                          toggleElementBorderRadius={5}
                          initialValue={filterSystemLogDropdown.selectedValue}
                          onPressSave={(selectedValue, startDate, endDate) => {
                            setFilterSystemLogDropdown({
                              ...filterSystemLogDropdown,
                              from: startDate,
                              to: endDate,
                              selectedValue: selectedValue,
                            });
                          }}
                        />
                      </div>
                    ]}
                    resetDefaultTable={() => {
                      const updateSystemLogFilter = {
                        ...filterSystemLog,
                        from: getDateFilterOption(dateFilter.baMuoiNgayGanNhat)[0].format('YYYY-MM-DD'),
                        to: getDateFilterOption(dateFilter.baMuoiNgayGanNhat)[1].format('YYYY-MM-DD'),
                        selectedValue: dateFilter.baMuoiNgayGanNhat,
                        stationId: '',
                      };
                      setSelectedChargeStationSystemLog(null);
                      setFilterSystemLogDropdown(updateSystemLogFilter);
                      setFilterSystemLog(updateSystemLogFilter);
                    }}
                    saveFilter={() => {
                      setFilterSystemLog(filterSystemLogDropdown);
                    }}
                    isDefaultFilter={Utils.checkDefaultFilter(
                      ['selectedValue', 'from', 'to', 'page', 'limit', 'agencyId'],
                      filterSystemLog,
                      dateFilter.baMuoiNgayGanNhat,
                    )}
                  />
                </div>
              </div>
              {systemLogList?.length
                ? (
                  <div className="px-4 mb-4">
                    <CustomCardMore
                      scrollTargetSelector=".system-log-scroll"
                      filter={filterSystemLog}
                      setFilter={setFilterSystemLog}
                      dataList={systemLogList}
                      dataTotal={totalSystemLog}
                      hasMore={systemLogList?.length < totalSystemLog}
                    >
                      <div
                        className="system-log-scroll"
                        style={{
                          maxHeight: '345px',
                          overflowY: 'auto',
                        }}
                      >
                        {systemLogList?.map((item, index) => {
                          return (
                            <div key={item?.messageId ?? index}>
                              {UIHelper.buildCellSystemInfo(
                                <i
                                  className={`${ChargeHelper.Utils.getMessageLogTypeIcon(
                                    item?.level
                                  )} text-${ChargeHelper.Utils.getMessageLogTypeColor(item?.level)}`}
                                  style={{
                                    fontSize: '22px',
                                  }}
                                />,
                                item?.title,
                                Utils.formatDateTimeToString(item?.createdAt, 'DD/MM/YYYY - HH:mm'),
                                true,
                                () => {
                                  Utils.openInNewTab(`${AppRoute.pointList}/${item?.pointId}`)
                                }
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </CustomCardMore>
                  </div>
                ) : (
                  <EmptyView
                    icon={imgEmptyNoData}
                    title="Không có dữ liệu để hiển thị"
                    text={<span>Vui lòng kiểm tra kết nối mạng hoặc tải lại trang.</span>}
                  />
                )}
            </div>,
            false,
            false,
            true,
            true
          )}
        </div>
      </div>

      {/* thong ke */}
      <div className="">
        <div className="px-4">
          {UIHelper.buildCard(
            <div className="">
              <div className="row m-0 p-0 px-2 pt-4">
                {/* cong suat tuc thi he thong */}
                <div className="col-xl-3 col-lg-6 mb-4 px-2">
                  {UIHelper.buildStatisticItem(
                    'rgba(0, 199, 190, 1)',
                    'Công suất tức thì của hệ thống',
                    `${Utils.isValid(systemStatistic?.totalPower)
                      ? Utils.formatNumber((systemStatistic?.totalPower / 1000).toFixed(3))
                      : 0
                    } kW`,
                    AppResource.icons.icTotalPower
                  )}
                </div>
                {/* san luong dien trong ngay */}
                <div className="col-xl-3 col-lg-6 mb-4 px-2">
                  {UIHelper.buildStatisticItem(
                    'rgba(83, 159, 248, 1)',
                    'Tổng sản lượng điện trong ngày',
                    `${Utils.isValid(systemStatistic?.totalEnergy)
                      ? Utils.formatNumber((systemStatistic?.totalEnergy / 1000).toFixed(3))
                      : 0
                    } kWh`,
                    AppResource.icons.icTotalEnergy
                  )}
                </div>
                {/* so phien sac trong ngay */}
                <div className="col-xl-3 col-lg-6 mb-4 px-2">
                  {UIHelper.buildStatisticItem(
                    'rgba(120, 118, 229, 1)',
                    'Tổng số phiên sạc trong ngày',
                    `${Utils.isValid(systemStatistic?.totalTransactions)
                      ? Utils.formatNumber(systemStatistic?.totalTransactions)
                      : 0
                    } phiên`,
                    AppResource.icons.icTotalTransactions
                  )}
                </div>
                {/* tong thoi gian sac trong ngay */}
                <div className="col-xl-3 col-lg-6 mb-4 px-2">
                  {UIHelper.buildStatisticItem(
                    'rgba(122, 214, 97, 1)',
                    'Tổng thời gian sạc trong ngày',
                    Utils.isValid(systemStatistic?.totalChargeDuration)
                      ? Utils.convertSecondsToHText(systemStatistic?.totalChargeDuration)
                      : '',
                    AppResource.icons.icTotalDuration
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* bieu do */}
      <div className="mt-4">
        <div className="row m-0 p-0 px-2">
          {/* thong ke dien sac */}
          <div className="col-lg-6 px-2 mb-4">{chartEnergyByDateEl}</div>
          {/* thong ke phien sac */}
          <div className="col-lg-6 px-2 mb-4">{chartTransactionByDateEl}</div>
          {/* thong ke doanh thu */}
          <div className="col-lg-6 px-2 mb-4">{chartEnergyRevenueByDateEl}</div>
          {/* thong ke trang thai tru sac */}
          <div className="col-lg-6 px-2 mb-4">
            {UIHelper.buildCard(
              <div className='h-100 d-flex flex-column justify-content-between'>
                <div>
                  {UIHelper.buildCardHeader(
                    'Thống kê trạng thái trụ sạc',
                    false,
                    filterChargeStationEl,
                    false,
                    true
                  )}
                  <div className="px-4 pb-4 d-flex align-items-center gap-3">
                    <div
                      className=""
                      style={{
                        height: '200px',
                        width: '200px',
                      }}
                    >
                      <DoughnutChartJS
                        showTitle={false}
                        showLegend={false}
                        labels={['Sẵn sàng', 'Đang bận', 'Đang bảo trì', 'Trụ lỗi', 'Mất kết nối']}
                        datasets={[
                          {
                            data: [
                              chargePointSummary[ChargeConstants.ChargePointStatus.AVAILABLE] ?? 0,
                              chargePointSummary[ChargeConstants.ChargePointStatus.BUSY] ?? 0,
                              chargePointSummary[ChargeConstants.ChargePointStatus.MAINTAINING] ?? 0,
                              chargePointSummary[ChargeConstants.ChargePointStatus.FAULT] ?? 0,
                              chargePointSummary[ChargeConstants.ChargePointStatus.DISCONNECTED] ?? 0,
                            ],
                            borderColor: [
                              'rgba(255, 255, 255, 1)',
                              'rgba(255, 255, 255, 1)',
                              'rgba(255, 255, 255, 1)',
                              'rgba(255, 255, 255, 1)',
                              'rgba(255, 255, 255, 1)',
                            ],
                            backgroundColor: [
                              'rgba(48, 189, 111, 1)',
                              'rgba(126, 130, 153, 1)',
                              'rgba(255, 157, 28, 1)',
                              'rgba(225, 78, 84, 1)',
                              'rgba(189, 48, 50, 1)',
                            ],
                            borderWidth: 3,
                          },
                        ]}
                      />
                    </div>
                    <div className="flex-fill">
                      {UIHelper.buildDoughnutChartLegend([
                        {
                          title: 'Sẵn sàng',
                          color: 'rgba(48, 189, 111, 1)',
                          value: chargePointSummary[ChargeConstants.ChargePointStatus.AVAILABLE] ?? 0,
                        },
                        {
                          title: 'Đang bận',
                          color: 'rgba(126, 130, 153, 1)',
                          value: chargePointSummary[ChargeConstants.ChargePointStatus.BUSY] ?? 0,
                        },
                        {
                          title: 'Đang bảo trì',
                          color: 'rgba(255, 157, 28, 1)',
                          value: chargePointSummary[ChargeConstants.ChargePointStatus.MAINTAINING] ?? 0,
                        },
                        {
                          title: 'Trụ lỗi',
                          color: 'rgba(225, 78, 84, 1)',
                          value: chargePointSummary[ChargeConstants.ChargePointStatus.FAULT] ?? 0,
                        },
                        {
                          title: 'Mất kết nối',
                          color: 'rgba(189, 48, 50, 1)',
                          value:
                            chargePointSummary[ChargeConstants.ChargePointStatus.DISCONNECTED] ?? 0,
                        },
                        {
                          title: 'Tổng số:',
                          value: _.sum(_.values(chargePointSummary)),
                        },
                      ])}
                    </div>
                  </div>
                </div>
                <div className='p-4'>
                  {UIHelper.buildNegativeButton(
                    'Danh sách trụ sạc',
                    () => {
                      Utils.openInNewTab(AppRoute.pointList);
                    }
                  )}
                </div>
              </div>,
              false,
              false,
              true,
              true
            )}
          </div>
        </div>
      </div>
    </div >
  );
}

export default InvestorDashboardScreen;
